import { useReviewerSchema } from "../../../../../server/auth/use-reviewer-schema";
import { useSupabaseLoader } from "../../../../../server/supabase/hooks";
import { SupabaseBrandKitService } from "../../../../brand-kit/server/brand-kit-service";
import { SupabaseAccountService } from "../../../../generic/server/supabase-account-service";

import type { UseSupabaseLoaderState } from "../../../../../server/supabase/hooks/types";
import type { UUID } from "../../../../../utils/uuid";

export function useLogo(): UseSupabaseLoaderState<{
  account_id: UUID;
  name: string;
  logo?: string;
}> {
  // A reviewer is granted neither the account nor the brand info, so the logo their page wears
  // comes through the reviewer schema.
  const reviewerSchema = useReviewerSchema();

  return useSupabaseLoader<
    { account_id: UUID; name: string; logo?: string },
    "public" | "reviewer"
  >(
    async ({ supabase, account_id }) => {
      if (reviewerSchema) {
        const { data, error } = await supabase
          .schema("reviewer")
          .rpc("branding", { account_id_: account_id })
          .single();

        if (error) throw error;

        return {
          account_id,
          name: data?.name ?? "",
          logo: data?.logo ?? undefined,
        };
      }

      const publicSupabase = supabase.schema("public");

      const [{ data: accountData }, { data: logoData }] = await Promise.all([
        new SupabaseAccountService(publicSupabase).get(account_id),
        new SupabaseBrandKitService(publicSupabase).getDefaultLogo(account_id),
      ]);

      return {
        account_id,
        name: accountData?.name ?? "",
        logo: logoData?.brand_logo?.src ?? undefined,
      };
    },
    [reviewerSchema],
    reviewerSchema ? "reviewer" : "public",
  );
}
